import { CaretRightOutlined, YoutubeOutlined } from "@ant-design/icons"
import { Alert, Button, Empty, Space, Switch, Tag, Typography } from "antd"
import type React from "react"
import { useEffect, useRef, useState } from "react"
import type { VideoSegment } from "@/services/video/videoApi"
import { activeSegmentIndex, formatVideoTime } from "../utils"

const { Text } = Typography

interface YouTubePlayer {
  destroy(): void
  getCurrentTime(): number
  getPlayerState(): number
  seekTo(seconds: number, allowSeekAhead: boolean): void
  playVideo(): void
}

interface YouTubeApi {
  Player: new (
    element: HTMLElement,
    options: {
      videoId: string
      width: string
      height: string
      playerVars: Record<string, string | number>
      events: { onReady: () => void; onError: (event: { data: number }) => void }
    }
  ) => YouTubePlayer
}

declare global {
  interface Window {
    YT?: YouTubeApi
    onYouTubeIframeAPIReady?: () => void
  }
}

let apiPromise: Promise<YouTubeApi> | undefined

function loadYouTube() {
  if (window.YT?.Player) return Promise.resolve(window.YT)
  if (apiPromise) return apiPromise
  apiPromise = new Promise<YouTubeApi>((resolve, reject) => {
    const previous = window.onYouTubeIframeAPIReady
    const script = document.createElement("script")
    window.onYouTubeIframeAPIReady = () => {
      previous?.()
      if (window.YT?.Player) resolve(window.YT)
      else {
        apiPromise = undefined
        reject(new Error("YouTube Player chưa sẵn sàng."))
      }
    }
    script.src = "https://www.youtube.com/iframe_api"
    script.async = true
    script.onerror = () => {
      apiPromise = undefined
      script.remove()
      reject(new Error("Không tải được YouTube Player."))
    }
    document.head.append(script)
  })
  return apiPromise
}

export const VideoPlayer: React.FC<{
  sourceId: string
  segments: VideoSegment[]
}> = ({ sourceId, segments }) => {
  const container = useRef<HTMLDivElement>(null)
  const transcript = useRef<HTMLDivElement>(null)
  const player = useRef<YouTubePlayer | null>(null)
  const segmentsRef = useRef(segments)
  const isAutoScrollingRef = useRef(false)
  const scrollTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)

  const [ready, setReady] = useState(false)
  const [error, setError] = useState("")
  const [active, setActive] = useState(-1)
  const [activeWord, setActiveWord] = useState(-1)
  const [showVietnamese, setShowVietnamese] = useState(true)
  const [autoScroll, setAutoScroll] = useState(true)

  segmentsRef.current = segments

  useEffect(() => {
    let disposed = false
    let instance: YouTubePlayer | null = null
    setReady(false)
    setError("")

    void loadYouTube()
      .then((api) => {
        if (disposed || !container.current) return
        const target = document.createElement("div")
        container.current.replaceChildren(target)
        instance = new api.Player(target, {
          videoId: sourceId,
          width: "100%",
          height: "100%",
          playerVars: { origin: window.location.origin, playsinline: 1 },
          events: {
            onReady: () => {
              if (!disposed) setReady(true)
            },
            onError: (event) => {
              if (!disposed)
                setError(
                  [101, 150].includes(event.data)
                    ? "Video không cho phép phát nhúng. Bạn có thể mở trực tiếp trên YouTube."
                    : "Video không khả dụng hoặc YouTube chặn phát nhúng trên trang này."
                )
            },
          },
        })
        player.current = instance
      })
      .catch((cause) => {
        if (!disposed) setError(cause instanceof Error ? cause.message : "Lỗi tải YouTube Player.")
      })

    const timer = setInterval(() => {
      const current = player.current
      if (!current?.getCurrentTime || disposed) return
      const time = current.getCurrentTime()
      const segmentIndex = activeSegmentIndex(segmentsRef.current, time)
      setActive(segmentIndex)
      setActiveWord(activeSegmentIndex(segmentsRef.current[segmentIndex]?.words || [], time))
    }, 250)

    return () => {
      disposed = true
      clearInterval(timer)
      instance?.destroy()
      player.current = null
    }
  }, [sourceId])

  // Detect manual user scrolling on the transcript container to turn off auto-scroll
  useEffect(() => {
    const list = transcript.current
    if (!list) return

    const onUserManualScroll = () => {
      isAutoScrollingRef.current = false
      clearTimeout(scrollTimeoutRef.current)
      setAutoScroll((prev) => (prev ? false : prev))
    }

    const onScroll = () => {
      if (isAutoScrollingRef.current) return
      setAutoScroll((prev) => (prev ? false : prev))
    }

    list.addEventListener("wheel", onUserManualScroll, { passive: true })
    list.addEventListener("touchmove", onUserManualScroll, { passive: true })
    list.addEventListener("scroll", onScroll, { passive: true })

    return () => {
      list.removeEventListener("wheel", onUserManualScroll)
      list.removeEventListener("touchmove", onUserManualScroll)
      list.removeEventListener("scroll", onScroll)
      clearTimeout(scrollTimeoutRef.current)
    }
  }, [])

  // Auto-scroll to active sentence when enabled
  useEffect(() => {
    if (!autoScroll) return
    const list = transcript.current
    if (!list || active < 0) return

    const row = list.querySelector<HTMLElement>(`[data-segment-index="${active}"]`)
    if (
      row &&
      (row.offsetTop < list.scrollTop || row.offsetTop + row.offsetHeight > list.scrollTop + list.clientHeight)
    ) {
      isAutoScrollingRef.current = true
      clearTimeout(scrollTimeoutRef.current)
      list.scrollTo({ top: Math.max(0, row.offsetTop - list.clientHeight / 3), behavior: "smooth" })
      scrollTimeoutRef.current = setTimeout(() => {
        isAutoScrollingRef.current = false
      }, 800)
    }
  }, [active, autoScroll])

  const seek = (index: number) => {
    const segment = segments[index]
    if (!segment || !ready) return
    player.current?.seekTo(segment.start, true)
    player.current?.playVideo()
  }

  return (
    <div className="flex flex-col gap-4">
      {error && (
        <Alert
          type="warning"
          showIcon
          message="Thông báo phát video"
          description={error}
          action={
            <Button
              type="primary"
              size="small"
              icon={<YoutubeOutlined />}
              href={`https://www.youtube.com/watch?v=${sourceId}`}
              target="_blank"
              rel="noreferrer"
            >
              Mở trên YouTube
            </Button>
          }
        />
      )}

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-12">
        {/* Left column: Video player + Controls */}
        <div className="flex flex-col gap-3 lg:col-span-7">
          <div className="relative aspect-video w-full overflow-hidden rounded-xl border border-gray-200 bg-black shadow-sm">
            <div ref={container} className="h-full w-full" />
          </div>

          <div className="rounded-xl border border-gray-200/80 bg-white p-3.5 shadow-2xs">
            <div className="flex flex-wrap items-center justify-between gap-3 text-sm">
              <Space size="middle" wrap>
                <label className="flex cursor-pointer select-none items-center gap-2">
                  <Switch size="small" checked={showVietnamese} onChange={setShowVietnamese} />
                  <span className="font-medium text-gray-700">Hiện tiếng Việt</span>
                </label>

                <label className="flex cursor-pointer select-none items-center gap-2">
                  <Switch size="small" checked={autoScroll} onChange={setAutoScroll} />
                  <span className="font-medium text-gray-700">Tự động cuộn theo câu</span>
                </label>
              </Space>
            </div>

            <div className="mt-2 text-gray-400 text-xs">
              💡 Bấm vào một câu thoại bất kỳ bên danh sách để phát ngay từ đoạn đó.
            </div>
          </div>
        </div>

        {/* Right column: Interactive Transcript */}
        <div className="flex flex-col rounded-xl border border-gray-200 bg-white shadow-2xs lg:col-span-5">
          {/* Transcript Header */}
          <div className="flex items-center justify-between border-gray-100 border-b p-3.5">
            <div className="flex items-center gap-2">
              <Text strong className="text-base text-gray-800">
                Lời thoại song ngữ
              </Text>
              <Tag color="blue">{segments.length} câu</Tag>
            </div>

            {active >= 0 && (
              <Tag color="cyan" className="m-0 font-mono text-xs">
                Đang phát #{active + 1}
              </Tag>
            )}
          </div>

          {/* Transcript List */}
          <div
            ref={transcript}
            className="flex-1 overflow-y-auto p-2"
            style={{ maxHeight: "calc(100vh - 380px)", minHeight: "350px" }}
          >
            {segments.length === 0 ? (
              <div className="p-8 text-center">
                <Empty description="Chưa có lời thoại" />
              </div>
            ) : (
              segments.map((segment, index) => {
                const isCurrent = index === active

                return (
                  <div
                    key={segment.id}
                    data-segment-index={index}
                    onClick={() => seek(index)}
                    className={`group relative mb-2 cursor-pointer rounded-lg border p-3 transition-all ${
                      isCurrent
                        ? "border-blue-400 bg-blue-50/70 shadow-xs"
                        : "border-gray-100 bg-white hover:border-blue-200 hover:bg-gray-50/70"
                    }`}
                  >
                    {/* Time & Segment Header */}
                    <div className="mb-1.5 flex items-center justify-between text-xs">
                      <span className="font-mono text-gray-400 group-hover:text-blue-600">
                        <CaretRightOutlined className="mr-1 text-gray-300 group-hover:text-blue-500" />
                        {formatVideoTime(segment.start)} → {formatVideoTime(segment.end)}
                      </span>

                      <div className="flex items-center gap-1.5">
                        <span className="font-mono text-[11px] text-gray-400">#{index + 1}</span>
                      </div>
                    </div>

                    {/* English sentence with Karaoke highlight */}
                    <div className="font-medium text-[15px] text-gray-800 leading-snug">
                      {segment.words.length
                        ? segment.words.map((word, wordIndex) => {
                            const isCurrentWord = isCurrent && wordIndex === activeWord
                            return (
                              <span
                                key={`${word.start}-${word.end}-${word.text}`}
                                className={`transition-colors ${
                                  isCurrentWord ? "rounded-xs bg-blue-600 px-1 font-semibold text-white shadow-2xs" : ""
                                }`}
                              >
                                {word.text}
                                {wordIndex < segment.words.length - 1 ? " " : ""}
                              </span>
                            )
                          })
                        : segment.text}
                    </div>

                    {/* Vietnamese translation */}
                    {showVietnamese && (
                      <div className="mt-1.5 font-normal text-[13px] text-blue-900/80 leading-normal">
                        {segment.translation ? (
                          <span>{segment.translation}</span>
                        ) : (
                          <span className="text-gray-400 italic">Chưa có bản dịch</span>
                        )}
                      </div>
                    )}
                  </div>
                )
              })
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
